import type { Dayjs } from 'dayjs';

import { useDateTimeConfig } from '@treneo/shared/contexts';
import type { DateTimeRangeValue, DateTimeValue } from '@treneo/shared/types';
import {
  convertDateFormatToDayjs,
  convertTimeFormatToDayjs,
  formatDayjsToString,
  isValidDayjs
} from '@treneo/shared/utils';

export const useDateTimePickerLogic = () => {
  const { dateFormat, timeFormat } = useDateTimeConfig();

  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

  const createDateTimeValue = (val: Dayjs | null): { date: string; time: string } => {
    const date = isValidDayjs(val) ? formatDayjsToString(val, dayjsDateFormat) : '';
    const time = isValidDayjs(val) ? formatDayjsToString(val, dayjsTimeFormat) : '';
    return { date, time };
  };

  const createDateValue = (val: Dayjs | null): string =>
    isValidDayjs(val) ? formatDayjsToString(val, dayjsDateFormat) : '';

  const createTimeValue = (val: Dayjs | null): string =>
    isValidDayjs(val) ? formatDayjsToString(val, dayjsTimeFormat) : '';

  const createDateTimeOnChange =
    <T extends DateTimeValue | DateTimeRangeValue>(
      currentValue: T,
      onChange: (value: T) => void,
      field: string
    ) =>
    (val: Dayjs | null) => {
      const { date, time } = createDateTimeValue(val);
      const update = {
        ...currentValue,
        [`${field}Date`]: date,
        [`${field}Time`]: time
      };
      onChange(update as T);
    };

  const createDateOnChange =
    <T extends DateTimeValue | DateTimeRangeValue>(
      currentValue: T,
      onChange: (value: T) => void,
      field: string,
      syncEndDate?: boolean
    ) =>
    (val: Dayjs | null) => {
      const dateStr = createDateValue(val);
      const update = { ...currentValue, [`${field}Date`]: dateStr };

      if (
        syncEndDate &&
        field === 'start' &&
        dateStr &&
        (currentValue as DateTimeRangeValue).endDate &&
        (currentValue as DateTimeRangeValue).endDate ===
          (currentValue as DateTimeRangeValue).startDate
      ) {
        (update as DateTimeRangeValue).endDate = dateStr;
      }

      onChange(update as T);
    };

  const createTimeOnChange =
    <T extends DateTimeValue | DateTimeRangeValue>(
      currentValue: T,
      onChange: (value: T) => void,
      field: string
    ) =>
    (val: Dayjs | null) => {
      const timeStr = createTimeValue(val);
      const update = { ...currentValue, [`${field}Time`]: timeStr };
      onChange(update as T);
    };

  return {
    dayjsDateFormat,
    dayjsTimeFormat,
    createDateTimeValue,
    createDateValue,
    createTimeValue,
    createDateTimeOnChange,
    createDateOnChange,
    createTimeOnChange
  };
};
